<template>
  <registration-layout ref="form">
    <form
      class="new-registration vue"
      @submit.prevent="submit"
    >
      <h2>Emprego e Renda</h2>
      <p class="subheading">
        Complemente seu cadastro para agilizar o processo de aprovação
      </p>

      <brazil-select-field
        name="income_source"
        input-id="user_income_source"
        label="Fonte da renda"
        :options="options.incomeSources"
        :validator-field="$v.income_source"
        @change="incomeSourceChange"
      />

      <!-- field spacer -->
      <div class="registration-field" />

      <brazil-text-field
        v-model="position"
        name="position"
        input-id="user_position"
        label="Profissão"
        maxlength="50"
        :validator-field="$v.position"
      />

      <brazil-text-field
        v-if="income_source != 'Retired'"
        v-model="employer"
        name="employer"
        input-id="user_employer"
        label="Empregador"
        maxlength="40"
        :validator-field="$v.employer"
      />

      <brazil-select-field
        name="time_at_job"
        input-id="user_time_at_job"
        label="Tempo no emprego atual"
        :options="options.timeAtJob"
        :validator-field="$v.time_at_job"
      />

      <brazil-text-field
        v-model="monthly_income"
        name="monthly_income"
        input-id="user_monthly_income"
        type="tel"
        label="Renda líquida mensal comprovada"
        placeholder="R$"
        :mask="monthlyIncomeMask"
        maxlength="6"
        :validator-field="$v.monthly_income"
      />

      <brazil-select-field
        name="income_payment_method"
        input-id="user_income_payment_method"
        label="Como você recebe seu pagamento?"
        :options="options.incomePaymentMethods"
        :validator-field="$v.income_payment_method"
      />

      <brazil-select-field
        name="paydate_ordinal"
        input-id="user_paydate_ordinal"
        label="Qual dia você recebe sua renda?"
        :options="options.paydateOrdinals"
        :validator-field="$v.paydate_ordinal"
      />

      <brazil-select-field
        name="net_worth"
        input-id="user_net_worth"
        label="Patrimônio"
        :options="options.netWorth"
        :validator-field="$v.net_worth"
      />

      <brazil-select-field
        name="loan_purpose"
        input-id="user_loan_purpose"
        label="Objetivo do Empréstimo"
        :options="options.loanPurposes"
        :validator-field="$v.loan_purpose"
      />

      <h2>
        Conta Bancária
        <brazil-v-tooltip
          tooltip-icon-id="bank_popover"
          title="Por que é necessário?"
        >
          Caso você seja aprovado, o empréstimo será creditado na conta corrente
          informada de forma automática e segura.
          <br>
          <br>É importante que você seja um dos titulares desta conta.
        </brazil-v-tooltip>
      </h2>
      <div class="important-message">
        <p>
          <strong>IMPORTANTE:</strong>
          o CPF informado deve ser do(a) titular da conta bancária informada
          <br>A conta bancária informada deve ser a mesma onde a renda é recebida.
        </p>
      </div>
      <transition name="bounce">
        <div
          v-if="bank_name && bank_name == '104'"
          class="registration-field full-width"
        >
          <brazil-v-tooltip
            ref="caixaSavingsTooltip"
            tooltip-icon-id="caixa_savings_popover"
            title="ATENÇÃO"
            type="modal"
          >
            As contas tipo poupança (013) e conta Caixa Fácil (023) foram renumeradas.
            <br><br>
            Essas contas passam a ter o código 1288 e o número da conta bancária foi alterado.
            <br><br>
            Para concluir a sua solicitação de empréstimo&nbsp;
            <strong>é necessário utilizar a nova numeração</strong>.
            <br><br>
            A nova numeração de sua conta aparece nas consultas no&nbsp;
            <a href="https://internetbanking.caixa.gov.br/sinbc/">Internet Banking CAIXA</a>,&nbsp;
            Aplicativo CAIXA, nos comprovantes dos Caixas Eletrônicos,&nbsp;
            Correspondentes CAIXA Aqui e Casas Lotéricas.
          </brazil-v-tooltip>
          <div
            class="caixa-bank-card content-box"
            style
          >
            <img
              src="~images/caixa_card.png"
              alt="Caixa card"
            >
          </div>
        </div>
      </transition>

      <brazil-select-field
        name="bank_name"
        input-id="user_bank_name"
        label="Banco onde você possui conta"
        :options="availableBanks"
        :validator-field="$v.bank_name"
        @change="bankNameChange"
      />

      <brazil-select-field
        v-if="bank_name && bank_name === '237'"
        name="bank_account_type"
        input-id="user_bank_account_type"
        label="Tipo de Conta"
        :options="options.bankAccountTypes[bank_name]"
        :validator-field="$v.bank_account_type"
      >
        <template v-slot:note>
          <div class="under-instructions">
            Apenas trabalhamos com os 2 tipos de conta listados abaixo
          </div>
        </template>
      </brazil-select-field>

      <brazil-select-field
        v-if="bank_name && bank_name === '104'"
        name="caixa_account_type"
        input-id="user_caixa_account_type"
        label="Tipo de Conta"
        :options="options.bankAccountTypes[bank_name]"
        :validator-field="$v.caixa_account_type"
        @change="caixaAccountTypeChange"
      >
        <template v-slot:note>
          <div
            v-if="(caixa_account_type === '013') || (caixa_account_type === '023')"
            class="under-instructions"
          >
            Verifique a nova numeração de sua conta no app da Caixa e selecione o tipo 1288
            (<a
              href="https://www.caixa.gov.br/voce/poupanca-e-investimentos/poupanca/aviso-importante/Paginas/default.aspx"
              target="_blank"
            >clique para saber mais</a>).
          </div>
          <div
            v-else
            class="under-instructions"
          >
            Trabalhamos apenas com os 4 tipos de conta listados acima.
          </div>
        </template>
      </brazil-select-field>

      <brazil-text-field
        v-model="bank_branch"
        name="bank_branch"
        input-id="user_bank_branch"
        type="tel"
        label="Agência"
        placeholder="1234"
        :mask="bankBranchMask"
        maxlength="4"
        :validator-field="$v.bank_branch"
      />

      <brazil-text-field
        v-if="bank_name &&
          (bank_name !== '104' || (caixa_account_type !== '1288' && caixa_account_type !== '3701'))"
        v-model="bank_account_number"
        name="bank_account_number"
        input-id="user_bank_account_number"
        type="tel"
        :label="banks[bank_name].accountLabel"
        :placeholder="banks[bank_name].placeholder"
        :mask="banks[bank_name].mask"
        :maxlength="banks[bank_name].maskedMaxLength"
        :padding-length="banks[bank_name].maskedMaxLength - 1"
        :padding-char="'0'"
        :validator-field="$v.bank_account_number"
        :validator-key="bank_name"
      />

      <brazil-text-field
        v-if="bank_name && bank_name === '104' &&
          (caixa_account_type === '1288' || caixa_account_type === '3701')"
        v-model="bank_account_number"
        name="bank_account_number"
        input-id="user_bank_account_number"
        type="tel"
        :label="banks[bank_name].accountLabel"
        :placeholder="banks[bank_name].placeholderLong"
        :mask="banks[bank_name].maskLong"
        :maxlength="banks[bank_name].maskedMaxLengthLong"
        :padding-length="banks[bank_name].numericAccountLengthLong"
        :padding-char="'0'"
        :validator-field="$v.bank_account_number"
        :validator-key="caixa_account_type"
      />

      <brazil-text-field
        v-if="bank_name === null"
        v-model="bank_account_number"
        name="bank_account_number"
        maxlength="13"
        input-id="user_bank_account_number"
        type="tel"
        label="Conta Corrente (com dígito de verificação)"
        :validator-field="$v.bank_account_number"
      />

      <hr>

      <div class="registration-field">
        <div class="form-group">
          * Preenchimento obrigatório
        </div>
      </div>

      <RegistrationNextButton
        ref="submitButton"
        :use-spinner="false"
        @click.prevent="submit"
      />
    </form>
    <LoadingSpinnerModal v-show="showRoutingModal" />
  </registration-layout>
</template>

<script>
// eslint-disable-next-line import/no-unresolved
import 'images/caixa_card.png';
import { sync, get, call } from 'vuex-pathify';
import { required, requiredIf } from 'vuelidate/lib/validators';
import numOfDaysInCurrentMonth from '../lib/numOfDaysInCurrentMonth';
import {
  bankBranchLength,
  monthlyIncomeLength,
  daysInCurrentMonth,
  bankAccountLength,
  caixaAccountType,
  moreThanJustNumbers,
  employerLength,
} from '../validators';
import store from '../store';
import LoadingSpinnerModal from '../components/LoadingSpinnerModal.vue';
import BrazilTextField from '../components/fields/BrazilTextField.vue';
import BrazilSelectField from '../components/fields/BrazilSelectField.vue';
import RegistrationNextButton from '../components/RegistrationNextButton.vue';
import BrazilVTooltip from '../components/BrazilVTooltip.vue';
import RegistrationLayout from '../layouts/RegistrationLayout.vue';
import {
  incomeSources,
  timeAtJob,
  incomePaymentMethods,
  paydateOrdinals,
  netWorth,
  loanPurposes,
  bankNames,
  bankAccountTypes,
} from '../config/options';
import banks from '../config/banks';
import { monthlyIncomeMask, bankBranchMask } from '../config/masks';
import captureRenderBlockingException from '../tracking/helpers/captureRenderBlockingException';
import { optimizelyInstance, optimizelyUserId, optimizelyUserContext } from '../lib/optimizely';
import redirectTo from '../lib/routerHelpers/redirectTo';
import tagRecordings from '../tracking/hotjar/tagRecordings';
import trackFormCompletedEvent from '../lib/analytics/trackHelpers/trackFormCompletedEvent';
import { trackMaxScrollDepth } from '../lib/analytics/trackHelpers/trackScrollDepth';
import {
  addAllBehaviorTrackingListeners,
  removeAllBehaviorTrackingListeners,
} from '../lib/analytics/trackHelpers/eventListeners';
import analytics from '../lib/analytics';

export default {
  components: {
    RegistrationLayout,
    RegistrationNextButton,
    BrazilTextField,
    BrazilSelectField,
    BrazilVTooltip,
    LoadingSpinnerModal,
  },
  data() {
    return {
      options: {
        incomeSources,
        timeAtJob,
        incomePaymentMethods,
        paydateOrdinals,
        netWorth,
        loanPurposes,
        bankNames,
        bankAccountTypes,
      },
      banks,
      monthlyIncomeMask,
      bankBranchMask,
      showRoutingModal: null,
      startTime:        new Date().toISOString(),
    };
  },
  computed: {
    ...sync('registration/reg3@*'),
    ...get(['backendErrors', 'backendErrorMessages']),
    submittingData:     get('registration/submittingRegData'),
    awaitingNavigation: get('awaitingNavigation'),
    availableBanks() { return this.filterAvailableBanks(); },
  },
  mounted() {
    try {
      this.$store.dispatch('registration/setCurrentStepNumber', 3);
      this.verifyEmployerFieldForExistingCustomers();
      // default paydate_ordinal to 1 if its value is greater then number of days in current month
      if (
        this.paydate_ordinal
        && this.paydate_ordinal > numOfDaysInCurrentMonth()
      ) {
        this.paydate_ordinal = 1;
      }

      if (this.backendErrorMessages) {
        this.flash(this.backendErrorMessages, 'error');
      }
      const bankName = this.$store.state.registration.reg3.bank_name;
      // eslint-disable-next-line max-len
      if (bankName && !this.availableBanks.find((bank) => bank.value.toString() === bankName.toString())) {
        this.$store.dispatch('registration/clearBank');
      }
      this.caixaAccountTypeChange(); // check for and handle discontinued savings account type
      analytics.page();
      addAllBehaviorTrackingListeners();
    } catch (e) {
      this.$store.dispatch('setBackendErrorMessage');
      this.flash(this.backendErrorMessages, 'error');
      const { loanApplication } = store.state;
      const loanOffers = typeof loanApplication !== 'undefined' && loanApplication !== null ? loanApplication.quotes : null;
      const extras = {
        loanApplication,
        loanOffers,
        sessionQuote: store.state.sessionQuote,
      };
      captureRenderBlockingException(e, extras);
    }
  },
  destroyed() {
    removeAllBehaviorTrackingListeners();
  },
  methods: {
    verifyEmployerFieldForExistingCustomers() {
      if (this.employer && this.employer.length > 40 && (this.$v.$invalid || this.$v.$pending)) {
        this.$v.$touch();
        this.$refs.submitButton.shakeDisableButton();
        this.$refs.form.focusFirstError();
      }
    },
    incomeSourceChange() {
      if (!this.availableBanks.find((b) => b.value === this.bank_name)) {
        this.bank_name = null;
      }
      this.employer = null;
    },
    bankNameChange() {
      this.caixa_account_type = null;
    },
    caixaAccountTypeChange() {
      // The 013 and 023 savings account types have been discontinued.
      // Force selection of 1288 when user attempts these types.
      if (this.caixa_account_type && (this.caixa_account_type === '013' || this.caixa_account_type === '023')) {
        this.caixa_account_type = '1288';
        if (this.$refs.caixaSavingsTooltip) {
          this.$refs.caixaSavingsTooltip.toggleTooltip();
        }
      }
    },
    filterAvailableBanks() {
      const allBankNames = this.options.bankNames;
      let availableBanks = [];

      if (optimizelyInstance.isFeatureEnabled('banco_do_brasil', optimizelyUserId(), optimizelyUserContext(this.income_source))) {
        availableBanks = allBankNames
          .filter((bankName) => bankName.incomeSources.includes(this.income_source));
      }

      if (availableBanks.length < 1) {
        return this.options.bankNames.filter((bankName) => bankName.value !== '1');
      }

      return availableBanks;
    },
    submitReg3: call('registration/submitReg3'),
    async submit() {
      if (this.$v.$invalid || this.$v.$pending) {
        this.$v.$touch();
        this.$refs.submitButton.shakeDisableButton();
        this.$refs.form.focusFirstError();
      } else {
        this.flash().destroyAll();
        try {
          this.showRoutingModal = true;
          const response = await this.submitReg3();

          if (response.status === 201) {
            const loanAppStatus = response.data.data.loanApplication.loan_application_status;
            if (loanAppStatus === 'underwriting_approved') {
              this.$router.push('/loan_applications/loan_offers');
              return;
            }

            if (loanAppStatus === 'applied') {
              tagRecordings(['ebs']);
              this.$router.push('/v/financeiro/ebs');
              return;
            }

            redirectTo(response.data.redirect, '/v/financeiro');
            return;
          }

          if (this.backendErrorMessages) {
            store.dispatch('registration/setSubmittingRegData', false);
            this.flash(this.backendErrorMessages.join('<br>'), 'error');
            return;
          }

          if (response.data.redirect) {
            redirectTo(response.data.data, '/v/financeiro');
            return;
          }

          this.flash(this.$t('shared.generic_error'), 'error');
        } catch (error) {
          this.flash(this.$t('shared.generic_error'), 'error');
        } finally {
          trackFormCompletedEvent('reg3', this.startTime);
          trackMaxScrollDepth('reg3');
          this.showRoutingModal = false;
        }
      }
    },
  },
  validations() {
    return {
      income_source: {
        required,
      },
      position: {
        required,
      },
      employer: {
        required: requiredIf(() => ['Government', 'Full-time'].includes(this.income_source)),
        moreThanJustNumbers,
        employerLength,
      },
      time_at_job: {
        required,
      },
      monthly_income: {
        required,
        monthlyIncomeLength,
      },
      income_payment_method: {
        required,
      },
      paydate_ordinal: {
        required,
        daysInCurrentMonth,
      },
      net_worth: {
        required,
      },
      loan_purpose: {
        required,
      },
      bank_name: {
        required,
      },
      bank_branch: {
        required,
        bankBranchLength,
      },
      bank_account_type: {
        required: requiredIf(() => this.bank_name === '237'),
      },
      caixa_account_type: {
        required:         requiredIf(() => this.bank_name === '104'),
        caixaAccountType: (this.bank_name === '104' ? caixaAccountType() : true),
      },
      bank_account_number: {
        required,
        bankAccountLength: bankAccountLength((this.bank_name === '104' && (this.caixa_account_type === '1288' || this.caixa_account_type === '3701') ? this.caixa_account_type : this.bank_name)),
      },
    };
  },
};
</script>
<style>
#caixa_savings_popover {
  display: block;
  margin: 0 auto;
  width: 1px;
}
</style>
<style scoped>
.bounce-enter-active {
  animation: bounce-in 0.5s;
}
.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}
form {
  padding: 1rem;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}
.subheading {
  color: #333333;
}
.form-group {
  color: #333333;
}
</style>
